/**
 * What the chat sends Eddy after a decision on the Items to save card.
 *
 * Approving does not save anything by itself — the route only flips the
 * proposal's status, and Eddy then does the writes with his own tools. So
 * when the user EDITED the list (FRONT-2321), Eddy must be handed the edited
 * list, or he saves his original one from memory of his own tool call. That
 * list comes from the server's response (what was actually stored), never
 * from the browser's local draft.
 *
 * One definition for all three surfaces that render the card (the chat
 * modal, the day-one chat, the phone), which each used to carry a copy of
 * these sentences.
 */
import type { SaveProposalItem } from '~/hooks/queries/use-save-proposal';

export const SAVE_PROPOSAL_APPROVED_MESSAGE =
  'I approved the save proposal in the UI — please save those items now using your memory/knowledge tools (no further confirmation needed).';

export const SAVE_PROPOSAL_DECLINED_MESSAGE = "I declined the save proposal. Acknowledge and don't save anything.";

function targetWords(target: SaveProposalItem['target']): string {
  return target === 'graph' ? 'knowledge graph' : 'note';
}

export function saveProposalApprovedMessage(editedItems?: SaveProposalItem[]): string {
  if (!editedItems) return SAVE_PROPOSAL_APPROVED_MESSAGE;
  const lines = editedItems.map((item) => `- (${targetWords(item.target)}) ${item.text}`).join('\n');
  return `I edited the save list before approving it in the UI. Save exactly these items, not the ones you originally proposed, using your memory/knowledge tools (no further confirmation needed):\n\n${lines}`;
}

export function saveProposalCommentMessage(comment: string): string {
  return `Here are my corrections to the save proposal:\n\n${comment}\n\nPlease discard the current proposal and re-propose a corrected save list. Don't save anything until I approve the new proposal.`;
}

/** The friendly label the phone shows in place of the instruction text. */
export function saveDecisionLabel(decision: 'approve' | 'reject', edited: boolean): string {
  if (decision === 'reject') return 'Declined the save proposal';
  if (edited) return 'Saved your edited version';
  return 'Approved the save proposal';
}
